import { GarageHeader } from '@/components/GarageHeader'
import { Hero } from '@/components/Hero'
import { LikeButton } from '@/components/LikeButton'
import { MapStatic } from '@/components/MapStatic'
import { PartsList } from '@/components/PartsList'
import { ProjectComment } from '@/components/ProjectComment'
import { ProjectCommentForm } from '@/components/ProjectCommentForm'
import { ProjectsList } from '@/components/ProjectsList'
import { VideoEmbed } from '@/components/VideoEmbed'
import memberProjectData from '@/data/memberProject'
import { laravelGraphqlEndpoint } from '@/globals/globals'
import { UserContext } from '@/lib/UserContext'
import { getPrefixedStorageURL } from '@/lib/getPrefixedStorageURL'
import { isImageFile } from '@/lib/isImageFIle'
import { nl2br } from '@/lib/nl2br'
import { parseJSONinObject } from '@/lib/parseJSONinObject'
import NotFoundPage from '@/pages/404'
import { Project } from '@/types/project'
import { ProjectComment as ProjectCommentType } from '@/types/projectComment'
import { Video } from '@/types/video'
import { ChatIcon, InfoIcon } from '@chakra-ui/icons'
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  HStack,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { gql, request } from 'graphql-request'
import { GetStaticProps } from 'next'
import NextLink from 'next/link'
import 'photoswipe/dist/photoswipe.css'
import { useContext, useEffect, useState } from 'react'
import { Gallery, Item } from 'react-photoswipe-gallery'

export async function getStaticPaths() {
  const { data: { projects } = { projects: [] } } = await request(
    laravelGraphqlEndpoint,
    gql`
      query {
        data: memberProjectsPaginated(first: 3000, published: true) {
          projects: data {
            slug
          }
        }
      }
    `
  )

  // check if we want to have a faster build
  const fullBuild = process.env.NEXT_BUILD_TYPE !== 'fast'

  // Build the params from the response object
  const paths = fullBuild
    ? projects.map((project: Project) => {
        return {
          params: {
            slug: project.slug,
          },
        }
      })
    : []

  return { paths, fallback: true }
}

export const getStaticProps: GetStaticProps = async context => {
  // Return 404 if no slug is passed
  if (!context.params?.slug) return { notFound: true }

  const post = await request(laravelGraphqlEndpoint, memberProjectData(), {
    slug: context.params?.slug,
  })

  let project = parseJSONinObject(post.memberProjectBySlug) as Project

  // Guard against no project
  if (!project) return { notFound: true }

  let make = project.vehicleMake
    ? await request(
        laravelGraphqlEndpoint,
        gql`
          query ($id: ID!) {
            carMakeByID(id_car_make: $id) {
              id_car_make
              name
            }
          }
        `,
        {
          id: project.vehicleMake,
        }
      )
    : null

  let model = project.vehicleModel
    ? await request(
        laravelGraphqlEndpoint,
        gql`
          query ($id: ID!) {
            carModelByID(id_car_model: $id) {
              id_car_model
              name
            }
          }
        `,
        {
          id: project.vehicleModel,
        }
      )
    : null

  // Determine not found
  let notFound = false

  if (!project) {
    notFound = true
  }
  if (!project.published) {
    notFound = true
  }
  if (context.preview) {
    notFound = false
  }

  return {
    props: { project, make, model, preview: context.preview ? true : false },
    revalidate: 5,
    notFound,
  }
}

type Props = {
  project: Project
  make?: any
  model?: any
  preview: boolean
}

const handleExitPreview = async () => {
  await fetch('/api/clear-preview-mode-cookies').then(() => {
    window.location.reload()
  })
}

export default function Post({ project, make, model, preview }: Props) {
  const userData = useContext(UserContext)
  const user = userData({ guarded: false })

  const [comments, setComments] = useState<ProjectCommentType[]>(
    project?.comments ?? []
  )

  // Update comments when project loads
  useEffect(() => {
    project?.comments && setComments(project.comments)
  }, [project?.comments])

  // Guard against no project
  if (!project) return null

  // Determine if user can see unpublished projects
  const canSeeUnpublished =
    user?.super === 'true' || (user?.id && user?.id === project.user?.id)

  // Determine if user can see project
  const canSeeProject = canSeeUnpublished || project.published

  // If user can't see project, redirect to 404 page
  if (!canSeeProject) return NotFoundPage()

  return (
    <>
      {canSeeUnpublished && preview && (
        <Stack
          direction='row'
          justify='center'
          gap='2'
          backgroundColor='green.400'
          color='white'
          p='2'
          textAlign='center'
          fontSize='sm'>
          <Box>{!project.published && 'Unpublished'}</Box>
          <Box>Preview mode</Box>
          <Button size='xs' colorScheme='green' onClick={handleExitPreview}>
            Exit Preview
          </Button>
        </Stack>
      )}
      {project.user && project.user.garages && project.user.garages[0] && (
        <GarageHeader garage={project.user.garages[0]} />
      )}
      <Hero heading={project.title}>
        <Stack justifyContent='center' p={2} pb={8} gap={2} direction='row'>
          {make?.carMakeByID?.name && (
            <Text>
              <NextLink
                href={`/nation/categories/make/${make.carMakeByID.id_car_make}`}
                passHref
                legacyBehavior>
                <Badge as='a'>{make?.carMakeByID?.name}</Badge>
              </NextLink>
            </Text>
          )}
          {model?.carModelByID?.name && (
            <Text>
              <NextLink
                href={`/nation/categories/model/${model.carModelByID.id_car_model}`}
                passHref
                legacyBehavior>
                <Badge as='a'>{model?.carModelByID?.name}</Badge>
              </NextLink>
            </Text>
          )}
          {project.vehicleYear && <Text>{project.vehicleYear}</Text>}
        </Stack>
        <Flex justifyContent='center' gap={6} alignItems='center' mb={2}>
          {project.commentsEnabled &&
            project.comments &&
            project.comments.length > 0 && (
              <Link href='#comments' display='flex' alignItems='center' gap={2}>
                <ChatIcon mr={2} width={7} height={7} />
                <Text
                  fontSize={24}
                  fontWeight='bold'
                  minW={4}
                  textAlign='right'>
                  {project.comments.length}
                </Text>
              </Link>
            )}
          <LikeButton projectUuid={project.uuid} />
        </Flex>
        {!user && (
          <Box pos='absolute' w='full' bottom={5} left={0} fontSize='sm'>
            <NextLink href='/register' passHref legacyBehavior>
              <Link textDecoration='underline'>Signup</Link>
            </NextLink>{' '}
            or{' '}
            <NextLink href='/login' passHref legacyBehavior>
              <Link textDecoration='underline'>Login</Link>
            </NextLink>{' '}
            to like and comment
          </Box>
        )}
      </Hero>
      <VStack spacing={12} py={{ base: 6, lg: 24 }}>
        {(project.car_show_categories ||
          project.car_show_categories_2021 ||
          project.awards) && (
          <HStack gap='8' wrap='wrap' justifyContent='center'>
            {project.awards &&
              project.awards.map((award, i) => (
                <Box key={i}>
                  <Image
                    src={`/img/awards/${award}.png`}
                    alt=''
                    height={{ base: '140', lg: '200' }}
                    objectFit='cover'
                    objectPosition='center'
                  />
                </Box>
              ))}
            {project.car_show_categories && (
              <Box>
                <Image
                  src='/img/car-show/iqcs-header-2.png'
                  alt=''
                  height={{ base: '140', lg: '200' }}
                  objectFit='cover'
                  objectPosition='center'
                />
              </Box>
            )}
            {project.car_show_categories_2021 && (
              <Box>
                <Image
                  src='/img/ics2021/logo/logo.png'
                  alt=''
                  height={{ base: '140', lg: '200' }}
                  objectFit='cover'
                  objectPosition='center'
                />
              </Box>
            )}
          </HStack>
        )}
        {project.videos &&
        project.videos?.length > 0 &&
        project.videos[0]?.embedURL ? (
          <Box width='100%'>
            <AspectRatio ratio={16 / 9}>
              <Box overflow='auto' width='100%'>
                <Grid
                  width={`${project.videos.length}00vw`}
                  height='100%'
                  overflow='auto'
                  gap={10}
                  templateColumns={`repeat(${project.videos.length}, 100vw)`}
                  bgColor='gray.100'>
                  {project.videos.map((video: Video, i: number) => (
                    <Box key={i}>
                      <VideoEmbed data={video} />
                    </Box>
                  ))}
                </Grid>
              </Box>
            </AspectRatio>
          </Box>
        ) : (
          project.videos &&
          project.videos[0]?.embedURL && (
            <Box width='100%'>
              <AspectRatio ratio={16 / 9}>
                <VideoEmbed data={project.videos[0]} />
              </AspectRatio>
            </Box>
          )
        )}
        <Container maxW='6xl'>
          <Gallery withCaption>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {project.images &&
                project.images.map(
                  (image: any, i: number) =>
                    isImageFile(image.path) && (
                      <Item
                        id={image.id}
                        original={`${getPrefixedStorageURL(
                          image.path,
                          'imgkit'
                        )}?tr=n-lightbox`}
                        thumbnail={`${getPrefixedStorageURL(
                          image.path,
                          'imgkit'
                        )}?tr=project_card`}
                        width={image.width ?? '1600'}
                        height={image.height ?? '1600'}
                        caption={image.description}
                        key={i}
                        cropped>
                        {({ ref, open }) => (
                          // @ts-ignore-next-line no-legacy-refs
                          <Box ref={ref} onClick={open} position='relative'>
                            <img
                              src={`${getPrefixedStorageURL(
                                image.path,
                                'imgkit'
                              )}?tr=n-project_card`}
                              title={image.description}
                              alt={image.description}
                              style={{ objectFit: 'cover' }}
                              width='500'
                              height='400'
                            />
                            {image.description && (
                              <Tooltip label={image.description}>
                                <InfoIcon
                                  backgroundColor='white'
                                  borderRadius='full'
                                  p='1px'
                                  position='absolute'
                                  bottom={2}
                                  right={2}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Item>
                    )
                )}
            </SimpleGrid>
          </Gallery>
        </Container>
        {project.mods && (
          <Container maxW='3xl'>
            <Heading as='h2' size='xs' color='gray.300' mb={4}>
              Mods
            </Heading>
            <Box dangerouslySetInnerHTML={{ __html: nl2br(project.mods) }} />
          </Container>
        )}
        {project.description && (
          <Container maxW='3xl'>
            <Heading as='h2' size='xs' color='gray.300' mb={4}>
              Build story
            </Heading>
            <Box
              dangerouslySetInnerHTML={{ __html: nl2br(project.description) }}
            />
          </Container>
        )}
        {project.partsList && project.partsList.length > 0 && (
          <Container maxW='4xl'>
            <PartsList partsList={project.partsList} />
          </Container>
        )}

        {project.lng && project.lat && (
          <MapStatic
            linkText='View Projects Map'
            linkUrl='/nation/map/projects'
            lat={Number(project.lat)}
            lng={Number(project.lng)}
          />
        )}

        {project.commentsEnabled && (
          <Container id='comments' maxW='xl' pb={1}>
            <Heading pt={12}>Comments</Heading>
            {comments &&
              comments.length > 0 &&
              comments.map(
                (comment: ProjectCommentType) =>
                  !comment.parent_uuid && (
                    <ProjectComment
                      user={user}
                      comment={comment}
                      allComments={comments}
                      key={comment.uuid}
                      project_uuid={project.uuid}
                      setComments={setComments}
                    />
                  )
              )}
            {user ? (
              <Box mt={6}>
                <Heading as='h3' mb={4} size='sm'>
                  Leave a comment on {project.title}
                </Heading>
                <ProjectCommentForm
                  user={user}
                  project_uuid={project.uuid}
                  setComments={setComments}
                  allComments={comments}
                />
              </Box>
            ) : (
              <Box mt={6}>
                <Heading as='h3' mb={4} size='sm'>
                  To leave a comment on {project.title}:
                </Heading>
                <NextLink
                  href={`/login?redirectTo=/nation/projects/${project.slug}#comments`}
                  passHref
                  legacyBehavior>
                  <Button as='a' colorScheme='red'>
                    Login
                  </Button>
                </NextLink>{' '}
                or{' '}
                <NextLink
                  href={`/register?redirectTo=/nation/projects/${project.slug}#comments`}
                  passHref
                  legacyBehavior>
                  <Button as='a' colorScheme='red'>
                    Signup
                  </Button>
                </NextLink>
              </Box>
            )}
          </Container>
        )}
      </VStack>
      <ProjectsList />
    </>
  )
}
